import type { AdjuntoItem } from '../services/visitas.service';

function fechaSubidaMs(adj: AdjuntoItem): number {
  const raw = adj.fecha_subida?.trim();
  if (!raw) return 0;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Identidad lógica del adjunto (mismo nombre + categoría = mismo respaldo),
 * aunque el servidor haya creado varias rutas tras reenvíos.
 */
function claveLogicaAdjunto(adj: AdjuntoItem): string {
  const nombre = (adj.nombre_archivo?.trim() ?? '').toLowerCase();
  const categoria = adj.id_categoria ?? '';
  if (nombre) {
    return `nombre:${nombre}|cat:${categoria}`;
  }
  const ruta = adj.ruta_almacenamiento?.trim();
  if (ruta) {
    return `ruta:${ruta}`;
  }
  return `meta:${categoria}|${adj.tamano_bytes ?? ''}`;
}

/**
 * Elimina adjuntos duplicados lógicos (p. ej. mismo test.pdf + Fotografía con distintas rutas).
 * Conserva el más reciente por `fecha_subida`; orden según primera aparición de cada clave.
 */
export function deduplicarAdjuntosVisita(
  adjuntos: AdjuntoItem[] | undefined | null
): AdjuntoItem[] | undefined {
  if (!adjuntos?.length) {
    return adjuntos ?? undefined;
  }

  const mejorPorClave = new Map<string, AdjuntoItem>();
  for (const adj of adjuntos) {
    const clave = claveLogicaAdjunto(adj);
    const prev = mejorPorClave.get(clave);
    if (!prev || fechaSubidaMs(adj) >= fechaSubidaMs(prev)) {
      mejorPorClave.set(clave, adj);
    }
  }

  const ordenClaves: string[] = [];
  for (const adj of adjuntos) {
    const clave = claveLogicaAdjunto(adj);
    if (!ordenClaves.includes(clave)) {
      ordenClaves.push(clave);
    }
  }

  const unicos = ordenClaves
    .map((clave) => mejorPorClave.get(clave))
    .filter((adj): adj is AdjuntoItem => adj != null);

  return unicos.length > 0 ? unicos : undefined;
}

/** Une adjuntos de raíz y registro_visita, luego deduplica por identidad lógica. */
export function resolverAdjuntosVisitaDesdeModelo(visita: {
  adjuntos?: AdjuntoItem[];
  registro_visita?: { adjuntos?: AdjuntoItem[] } | null;
}): AdjuntoItem[] | undefined {
  const merged = [...(visita.adjuntos ?? []), ...(visita.registro_visita?.adjuntos ?? [])];
  if (!merged.length) {
    return undefined;
  }
  return deduplicarAdjuntosVisita(merged);
}
